// Where the game comes from. Both kinds expose the same surface to the rest of the client:
//   view       the state to draw (a full simulation state locally, a rebuilt one online)
//   send(cmd)  a player command      tick(dt)  advance time      drain()  game events since last call
import { G, setState, createGame, startMatch, update, defaultName, ensureGeom, trainSeg, CFG } from '../sim/game.js';
import { applyCommand } from '../sim/commands.js';
import { buildMap, buildView, finishView, PROTOCOL } from '../sim/serialize.js';

// solo play and the title-screen demo: the simulation runs right here in the page
// your name from the title screen, so solo games label your city the way online ones do
const soloName = () => { try { return (localStorage.getItem('trammed-name') || '').trim().slice(0, 16) || 'Player'; } catch { return 'Player'; } };

export class LocalGame {
  constructor({ seed, np, demo = false, cheats = false, mods = [], level = 'normal' }) {
    this.kind = 'local'; this.demo = demo; this.seed = seed; this.np = np; this.cheats = cheats; this.mods = mods; this.level = level;
    this.speed = demo ? 2 : 1; this.paused = false; this.hostSeat = 0;
    const seats = Array.from({ length: np }, (_, i) => (i === 0 && !demo ? { name: soloName(), human: true } : { name: defaultName(i) }));
    this.state = createGame(seed, seats, { cheats, mods, level });
  }
  get view() { return this.state; }
  get isHost() { return true; }
  send(cmd) { setState(this.state); applyCommand(0, cmd); }
  start() { setState(this.state); startMatch(); }
  tick(dt) {
    setState(this.state);
    if (this.state.phase === 'play' && !this.paused) for (let i = 0; i < this.speed; i++) update(dt);
  }
  drain() { const ev = this.state.events; this.state.events = []; return ev; }
  close() {}
}

const wsURL = () => {
  const forced = new URLSearchParams(location.search).get('server');
  if (forced) return forced;
  const dev = import.meta.env && import.meta.env.DEV;
  return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${dev ? location.hostname + ':8080' : location.host}/ws`;
};

// online play: the server owns the game; this rebuilds what it sends and fills the gaps between updates
export class OnlineGame {
  constructor(handlers) {
    this.kind = 'online'; this.h = handlers; this.speed = 1; this.paused = false;
    this.mapData = null; this.state = null; this.lobby = null; this.events = []; this.seat = null; this.code = null;
    this.retry = 0; this.closed = false; this.pending = [];
    this.connect();
  }
  connect() {
    this.ws = new WebSocket(wsURL());
    this.ws.onopen = () => {
      this.retry = 0;
      const saved = this.saved();
      if (saved && this.wantRejoin) this.raw({ t: 'rejoin', code: saved.code, token: saved.token });
      for (const m of this.pending.splice(0)) this.raw(m);
      this.h.status?.('online');
    };
    this.ws.onmessage = e => { try { this.onMessage(JSON.parse(e.data)); } catch (err) { console.error(err); } };
    this.ws.onclose = () => {
      if (this.closed) return;
      // try to come back: 0.5s, 1s, 2s ... capped at 8s, rejoining the same seat
      this.h.status?.('reconnecting');
      this.wantRejoin = !!this.code;
      setTimeout(() => this.connect(), Math.min(8000, 500 * 2 ** this.retry++));
    };
  }
  saved() { try { return JSON.parse(sessionStorage.getItem('trammed-seat') || 'null'); } catch { return null; } }
  raw(msg) { if (this.ws.readyState === 1) this.ws.send(JSON.stringify(msg)); else this.pending.push(msg); }
  create(name, players, mods, level) { this.raw({ t: 'create', name, players, mods, level, cheats: new URLSearchParams(location.search).has('dev') }); }
  join(code, name) { this.raw({ t: 'join', code: code.trim().toUpperCase(), name }); }
  rejoinSaved() { const s = this.saved(); if (s) { this.wantRejoin = true; this.raw({ t: 'rejoin', code: s.code, token: s.token }); } }
  host(t, extra = {}) { this.raw({ t, ...extra }); }
  send(cmd) { this.raw({ t: 'cmd', cmd }); }
  start() { this.host('start'); }
  get isHost() { return this.lobby && this.lobby.hostSeat === this.seat; }
  get view() { return this.state; }
  onMessage(m) {
    if (m.t === 'joined') {
      if (m.protocol !== PROTOCOL) { this.h.error?.('The server is running a different version. Reload the page.'); return; }
      this.seat = m.seat; this.code = m.code;
      try { sessionStorage.setItem('trammed-seat', JSON.stringify({ code: m.code, token: m.token })); } catch { /* storage blocked */ }
      this.h.joined?.(m);
    } else if (m.t === 'map') {
      this.mapData = buildMap(m);
      if (this.state) { this.state.map = this.mapData.map; this.state.regions = this.mapData.regions; this.state.mapV = m.mapV; }
    } else if (m.t === 'lobby') {
      this.lobby = m; m.startsAt = m.startsIn == null ? null : Date.now() + m.startsIn;
      if (m.phase === 'draft' && this.mapData) {
        // before the match there are no snapshots: draw the map and the claims from the lobby
        this.state = { phase: 'draft', map: this.mapData.map, regions: this.mapData.regions, mapV: this.mapData.mapV, seats: m.seats, np: m.np,
          players: [], stations: new Map(), flights: [], events: [], rd: {}, xq: {}, netV: 0, geoV: 0, time: 0, cheats: m.cheats, mods: m.mods || [] };
      }
      this.h.lobby?.(m);
    } else if (m.t === 'snap') {
      if (!this.mapData) return;
      const V = buildView(m, this.mapData, this.state && this.state.phase !== 'draft' ? this.state : null);
      setState(V); finishView(V);
      this.state = V;
      this.events.push(...(m.events || []));
    } else if (m.t === 'error') {
      if (/ended|closed|Could not find/.test(m.msg)) { try { sessionStorage.removeItem('trammed-seat'); } catch { /* storage blocked */ } this.code = null; }
      this.h.error?.(m.msg);
    }
  }
  // between server updates, keep trains and shapes gliding at their known speeds
  tick(dt) {
    const V = this.state;
    if (!V || V.phase !== 'play') return;
    setState(V);
    V.time += dt;
    ensureGeom();
    for (const p of V.players) for (const L of p.lines) for (const T of L.trains) {
      if (T.to == null || L.st.length < 2) continue;
      const sg = trainSeg(L, T.idx, T.to);
      if (!sg.geo) continue;
      const step = CFG.trainSpeed * dt / Math.max(1, sg.geo.len);
      T.t = Math.min(0.999, T.t + step);   // best guess at where the server has it now
      // updates arrive unevenly, so the guess is sometimes ahead of the next one; never pull a train back:
      // a train the server says is behind eases off until it catches up, one that is behind speeds up
      const gap = T.t - T.vt;
      if (Math.abs(gap) * sg.geo.len > 60) T.vt = T.t;
      else T.vt = Math.min(0.999, T.vt + step * (gap >= 0 ? 1 + Math.min(1, gap * sg.geo.len / 20) : Math.max(0, 1 + gap * sg.geo.len / 20)));
    }
    for (const f of V.flights) f.t = Math.min(0.999, f.t + dt / f.dur);
  }
  drain() { const ev = this.events; this.events = []; return ev; }
  leave() { this.raw({ t: 'leave' }); try { sessionStorage.removeItem('trammed-seat'); } catch { /* storage blocked */ } this.code = null; }
  close() { this.closed = true; try { this.ws.close(); } catch { /* already closed */ } }
}

// the front page's view of quick play: a socket that only listens to the list of open public lobbies
export class LobbyBrowser {
  constructor(onList, onDown) {
    this.onList = onList; this.onDown = onDown; this.closed = false; this.retry = 0;
    this.connect();
  }
  connect() {
    try { this.ws = new WebSocket(wsURL()); } catch { this.onDown?.(); return; }
    this.ws.onopen = () => { this.retry = 0; this.ws.send(JSON.stringify({ t: 'browse' })); };
    this.ws.onmessage = e => {
      try {
        const m = JSON.parse(e.data);
        if (m.t === 'lobbies') { const at = Date.now(); this.onList(m.list.map(l => ({ ...l, startsAt: at + l.startsIn }))); }
      } catch { /* ignore a bad frame */ }
    };
    this.ws.onclose = () => {
      if (this.closed) return;
      this.onDown?.();
      setTimeout(() => !this.closed && this.connect(), Math.min(15000, 1000 * 2 ** this.retry++));
    };
  }
  close() { this.closed = true; try { this.ws.close(); } catch { /* already closed */ } }
}
